import { CircularProgress, Divider, IconButton, SwipeableDrawer, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { ArrowForward } from '@material-ui/icons';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import React, { useEffect, useState } from 'react';
import { Redirect } from "react-router-dom";
import { Mixpanel } from '../mixpanel';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { categoryMap } from 'common-ts/dist/models/CategoryList';
import { cancelUserOrders, resetCartAddressAndPaymentState, updateUserTrigger } from '../redux/CartRedux';
import { fetchProductsList } from '../redux/ProductsListRedux';
import { openOverlay } from '../redux/UIOverlayAccountViewRedux';
import { updateCurrentScreen } from '../redux/UIOverlayNavbarRedux';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import CartContent from './CartContent';
import CartContentEmpty from './CartContentEmpty';
import { FareastButton } from './OverlayComponentButton';
import axios from 'axios';
import { sendWebsiteEventMessage, sendWebsiteLivestreamMessage } from '../telegrambotevents';
import { postNewProductAnalyticsLog } from '../redux/ProductAnalyticsRedux';
import { openOverlayMeaskitPrize } from '../redux/UIOverlayFlowVersionRedux';

const useStyles = ({forceFullWidth}: {forceFullWidth: boolean}) => makeStyles((theme: Theme) =>
  createStyles({
    cartWindow: {
      width: '100vw',
      height: '100%',
      background: '#fafafa',
      [theme.breakpoints.up('sm')]: {
        width: forceFullWidth ? '100%' : '480px',
      },
    },
    cartWindowContent: {
      textAlign: 'center',
      background: '#fafafa',
      padding: '0px 24px',
      width: 'auto',
      height: 'auto',
    },
    arrowHideButton: {
      position: 'absolute',
      left: '15px',
      paddingTop: '10px',
    },
    cartTitleOrLogo: {
      position: 'relative',
      display: 'inline-block',
      paddingTop: '30px',
      paddingBottom: '6px',
    },
    icon: {
      color: '#000',
    },
    dividerCart: {
      marginTop: '20px',
      marginBottom: '20px',
    },
    checkOutButton: {
      marginTop: '20px',
    },
    checkoutCalculateShippingText: {
      fontSize: '12px',
      marginTop: '16px',
      marginBottom: '-8px',
    }
  }),
);

interface CartWindowProps {
  navState: string,
  variant?: "drawer" | "inline",
  handleNext?: () => void,
  fullWidth?: boolean,
}

function CartWindow({ navState, variant, handleNext, fullWidth }: CartWindowProps) {
  const productsList = useAppSelector((state) => state.productsList);
  const cart = useAppSelector((state) => state.cart);
  const isLoggedIn = useAppSelector((state) => state.account.authState === 'signedin');
  const userTriggerCheckOutStatus = useAppSelector(state => state.cart.userTriggerCheckout);
  const userBodyMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userBodyMeasurementsDictionary);
  const userClothingMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userClothingMeasurementsDictionary);
  const userAccount = useAppSelector(state => state.account);
  const userIsSignedInAndHasPurchased = useAppSelector(state => state.account.authState === 'signedin' && (state?.account.paidOrderCount ?? 0) >= 1);
  const giveMeasKit = userIsSignedInAndHasPurchased === false && (cart?.cartProductList?.length ?? 0) > 0;
  const dispatch = useAppDispatch();
  const classes = useStyles(fullWidth !== undefined ? {forceFullWidth: fullWidth} : {forceFullWidth: false})();
  
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  // We reset cart address and payment if there is any.
  if (cart.orderStatus !== 'unsubmitted' || cart.address !== undefined 
  || cart.destinationObject !== undefined || cart.destinationKiriminAjaObject !== undefined) {
    console.log("Reset cart state");
    dispatch(resetCartAddressAndPaymentState());
  }

  // TODO Check if all items are in stock
  let allItemsInStockApproximate = true;

  // Default status is API success and user trigger false.
  // When the check out button is clicked, it becomes API request in progress
  // and user trigger true. This will not be processed yet. 
  // After API is success / productlist is updated, if all items in stock, checkout.
  // If some are missing, turn off user trigger and ask user to "fix" the cart.
  if (userTriggerCheckOutStatus) {
    if (allItemsInStockApproximate) {
      console.log("User is " + (isLoggedIn ? '' : "NOT") + " logged in. All items in stock. Proceeding to checkout");
      sendWebsiteLivestreamMessage("[CHECKOUT] User is " + (isLoggedIn ? '' : "NOT") + " logged in. All items in stock. Proceeding to checkout.", 1);
      dispatch(resetCartAddressAndPaymentState());

      // We cancel the user's all other orders here
      if (isLoggedIn) {
        dispatch(cancelUserOrders());
      }

      if (variant === "inline" && handleNext) {
        // 1. Turn off the trigger so we don't loop
        dispatch(updateUserTrigger(false)); 
        // 2. Tell Parent to switch to AddressInput/Checkout View
        handleNext(); 
        // 3. Return null to prevent rendering the Redirect component
        return null; 
      }

      return (<Redirect to={categoryMap["CHECK OUT"] }/>);
    } else {
      console.log('Stock is not there. Reverting user');
      dispatch(updateUserTrigger(false));
    }
  }

  function handleCheckoutRequest() {
    (window as any).dataLayer.push({'event': 'checkOut'});
    Mixpanel.track('checkOut');

    let contentsList = [];
    let contentIds = [];
    let numberOfProducts = 0;
    let totalValue = 0;

    let itemsInCartString: string = "";
    for (let cartIndex = 0; cartIndex < cart.cartProductList.length; cartIndex++) {
      let cartItem = cart.cartProductList[cartIndex];
      const productDetail = productsList.productsDictionary[cartItem.productId];
      itemsInCartString = itemsInCartString + productDetail.name + " sebanyak " + cartItem.amount + "; "

      contentsList.push({
        id: productDetail.productId,
        quantity: cartItem.amount
      });
      contentIds.push(productDetail.productId);
      numberOfProducts += cartItem.amount;
      totalValue += Math.round((productDetail.discountedPrice === -1 ? productDetail.originalPrice : productDetail.discountedPrice) * 0.7); // Multiplication factor

      dispatch(postNewProductAnalyticsLog({event: 'CHECKOUT', productId: cartItem.productId, isMobileView: !isDesktop }))
    }

    if ((window as any).fbq !== null) { 
      (window as any).fbq('track', 'InitiateCheckout', {
        content_ids: contentIds,
        contents: contentsList,
        content_type: 'product',
        currency: 'IDR',
        num_items: numberOfProducts,
        value: totalValue,
      });
    }

    sendWebsiteEventMessage("Ada yang check out (" + (isLoggedIn ? "" : "NOT ") + "signed in). Item di cart: " + itemsInCartString);
    sendWebsiteLivestreamMessage("Ada yang check out (" + (isLoggedIn ? "" : "NOT ") + "signed in). Item di cart: " + itemsInCartString, 1);
    dispatch(updateUserTrigger(true));
  }

  const cartElement = (
    <div className={classes.cartWindow}>
      <div className={classes.cartWindowContent}>
        <div className={classes.arrowHideButton}>
          <IconButton
            edge="end"
            aria-label="close cart"
            color="primary"
            onClick={() => {
              dispatch(updateCurrentScreen(navState === "CART" ? "DEFAULT" : "CART")); 
              (window as any).dataLayer.push({'event': 'navigateRoute', 'routePath': '/', 'routeSource': 'cartWindow'});
              Mixpanel.track('navigateRoute', {
                'routePath': '/',
                'routeSource': 'cartWindow',
              });
              Mixpanel.track('cartClose', {
                'routeSource': 'cartWindow',
              });
            }}
            className={classes.icon}
          >
            <ArrowForward />
          </IconButton>
        </div>
        <div className={classes.cartTitleOrLogo}>
          <IconButton
                  aria-label="cart icon"
                  color="primary"
                  className={classes.icon}
                >
            <ShoppingCartOutlinedIcon />
          </IconButton>
        </div>
        <Typography variant="body1">
          <b>
            YOUR CART
          </b>
        </Typography>
        <Divider className={classes.dividerCart} />

        {
          cart.cartProductList.length === 0 ? 
          <CartContentEmpty /> :
          <div>
            <CartContent cart={cart} productsList={productsList} isModifiable={true} />
            <Typography className={classes.checkoutCalculateShippingText}>
              Check out untuk menghitung ongkos kirim.
            </Typography>
            <FareastButton
              aria-label="check out"
              onClick={() => {
                handleCheckoutRequest();
              }}
              fullWidth
              buttonMarginLeftRight='0px'
              buttonMarginTopBottom='20px'
            >
              <Typography>
                <b>CHECK OUT</b>
              </Typography>
            </FareastButton>
          </div>
        }
      </div>
    </div>
  );

  if (variant === "inline") {
    return (<>{cartElement}</>);
  }

  return (
    <SwipeableDrawer
      anchor="right"
      open={navState === "CART"}
      onClose={(event) => dispatch(updateCurrentScreen("DEFAULT"))}
      onOpen={(event) => dispatch(updateCurrentScreen("CART"))}
    >
      {cartElement}
    </SwipeableDrawer>
  );
}

export default CartWindow;